import { INTL_TAG, type Locale, type MessageKey } from './catalogs';
import type { LocaleContextValue } from './LocaleProvider';

// ─────────────────────────────────────────────────────────────────────
// «PRØV IGJEN OM 12 MINUTTER» — THE ACTUAL WAIT, IN WORDS (#778 Q7).
//
// The owner's decision: a throttled person is told the real wait, not «om
// litt». Since the two-tier login budget (#778 Q1) that wait can be most of an
// hour, and «litt» for fifty-eight minutes sends somebody back to a door that
// will refuse them again and again.
//
// THE ROUNDING, and why:
//
//   unknown (0)   «om litt» — no header, or one we could not read. Never an
//                 invented number (`lib/api/retryAfter.ts`).
//   < 60 s        seconds, as given. A countdown under a minute is worth
//                 showing exactly; it is about to be over.
//   < 55 min      minutes, ROUNDED UP. Rounding down would tell somebody to
//                 come back before the server will let them: 61 s is «2
//                 minutter», because at 1 minute they would still be refused.
//   55–75 min     «omtrent en time». The login ceiling's window is an hour, so
//                 this is the band its refusals land in, and «59 minutter» is
//                 false precision for a number that is about to tick anyway.
//   longer        hours, rounded up. No budget is that long today; the band
//                 exists so a future one does not read «120 minutter».
//
// The sentence is one catalog entry per unit and plural form — whole
// sentences, not fragments glued together — so a translator sees what is
// said. The plural form comes from `Intl.PluralRules` for the locale, not
// from `count === 1`, which is true of Norwegian and English and of nothing
// in general.
// ─────────────────────────────────────────────────────────────────────

export type RetryWaitUnit = 'unknown' | 'seconds' | 'minutes' | 'aboutAnHour' | 'hours';

/** The unit and the number a wait of `seconds` is shown as. */
export function retryWaitParts(seconds: number): { unit: RetryWaitUnit; count: number } {
  if (!Number.isFinite(seconds) || seconds <= 0) return { unit: 'unknown', count: 0 };
  // Whole seconds first, so 59.5 is «1 minutt» rather than «60 sekunder».
  const whole = Math.ceil(seconds);
  if (whole < 60) return { unit: 'seconds', count: whole };
  const minutes = Math.ceil(whole / 60);
  if (minutes < 55) return { unit: 'minutes', count: minutes };
  if (minutes <= 75) return { unit: 'aboutAnHour', count: 1 };
  return { unit: 'hours', count: Math.ceil(minutes / 60) };
}

const KEYS: Record<RetryWaitUnit, { one: MessageKey; other: MessageKey }> = {
  unknown: { one: 'retryWait.unknown', other: 'retryWait.unknown' },
  seconds: { one: 'retryWait.seconds.one', other: 'retryWait.seconds.other' },
  minutes: { one: 'retryWait.minutes.one', other: 'retryWait.minutes.other' },
  aboutAnHour: { one: 'retryWait.aboutAnHour', other: 'retryWait.aboutAnHour' },
  hours: { one: 'retryWait.hours.other', other: 'retryWait.hours.other' },
};

/**
 * «For mange forsøk. Prøv igjen om 12 minutter.» in the locale's words.
 *
 * Takes the translator and the locale rather than reaching for the context, so
 * a surface that already has `useLocale()` passes what it has, and a test can
 * call it without rendering anything.
 */
export function retryWaitMessage(
  t: LocaleContextValue['t'],
  locale: Locale,
  seconds: number,
): string {
  const { unit, count } = retryWaitParts(seconds);
  const form = new Intl.PluralRules(INTL_TAG[locale]).select(count) === 'one' ? 'one' : 'other';
  const key = KEYS[unit][form];
  return unit === 'unknown' || unit === 'aboutAnHour' ? t(key) : t(key, { count });
}
